import { useCallback, useEffect, useState } from "react";
import api, { API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Layers, Plus, Check, Loader2, FileText, GitCompare, Star, Pencil, Trash2,
  ArrowRight, CircleHelp, ExternalLink,
} from "lucide-react";
import { toast } from "sonner";

const money = (v) => (v === null || v === undefined ? "—" : `$${Number(v).toFixed(2)}`);
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "");

export function DeductionLetterVersions({ propertyId, archived }) {
  const [versions, setVersions] = useState([]);
  const [current, setCurrent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [attachOpen, setAttachOpen] = useState(false);
  const [confirmFor, setConfirmFor] = useState(null);
  const [compareOpen, setCompareOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/properties/${propertyId}/deduction-versions`);
      setVersions(data.versions || []);
      setCurrent(data.current_version_id || null);
    } catch (e) { /* premium/ownership handled by parent gate */ }
    finally { setLoading(false); }
  }, [propertyId]);

  useEffect(() => { load(); }, [load]);

  const setCurrentVersion = async (versionId) => {
    try {
      await api.post(`/properties/${propertyId}/deduction-versions/current`, { version_id: versionId });
      setCurrent(versionId);
      toast.success("Current working version updated.");
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't update the selection."); }
  };

  const confirmedCount = versions.filter((v) => v.status === "confirmed").length;

  return (
    <div className="mt-6 rounded-2xl border border-[#E2E8F0] bg-[#F8FAFC] p-5 sm:p-6" data-testid="deduction-versions-card">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2 min-w-0">
          <Layers size={18} className="text-brand shrink-0" />
          <h3 className="font-heading text-lg font-semibold text-navy">Received letter versions</h3>
        </div>
        <div className="flex items-center gap-2">
          {confirmedCount >= 2 && (
            <Button size="sm" variant="outline" className="rounded-lg" data-testid="dlv-compare-btn" onClick={() => setCompareOpen(true)}>
              <GitCompare size={14} className="mr-1.5" /> Compare
            </Button>
          )}
          {!archived && (
            <Button size="sm" className="rounded-lg" data-testid="dlv-attach-btn" onClick={() => setAttachOpen(true)}>
              <Plus size={14} className="mr-1.5" /> Attach a letter
            </Button>
          )}
        </div>
      </div>
      <p className="mt-2 text-sm text-[#475569]">
        Attach each received deduction letter or itemized list as its own version. Every version keeps its original
        document and import details, and you can compare versions factually.
      </p>

      {loading ? (
        <div className="mt-4 flex items-center gap-2 text-sm text-[#64748B]"><Loader2 className="animate-spin" size={16} /> Loading…</div>
      ) : versions.length === 0 ? (
        <p className="mt-4 text-sm text-[#94A3B8]" data-testid="dlv-empty">No letter versions attached yet.</p>
      ) : (
        <ol className="mt-4 space-y-3">
          {versions.map((v) => (
            <li key={v.version_id} data-testid={`dlv-version-${v.version_id}`}
              className="rounded-xl border border-[#E2E8F0] bg-white p-4">
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="inline-flex items-center justify-center h-6 min-w-6 px-1.5 rounded-full bg-navy text-white text-xs font-bold">{v.seq}</span>
                    <span className="font-semibold text-navy truncate">{v.label}</span>
                    {v.status === "confirmed"
                      ? <span data-testid={`dlv-status-${v.version_id}`} className="inline-flex items-center gap-1 rounded-full bg-[#DCFCE7] text-[#15803D] text-xs font-bold px-2 py-0.5"><Check size={11} /> Confirmed</span>
                      : <span data-testid={`dlv-status-${v.version_id}`} className="inline-flex items-center rounded-full bg-[#FEF3C7] text-[#92400E] text-xs font-bold px-2 py-0.5">Draft — needs confirmation</span>}
                    {current === v.version_id && (
                      <span data-testid={`dlv-current-${v.version_id}`} className="inline-flex items-center gap-1 rounded-full bg-[#EFF6FF] text-[#1D4ED8] text-xs font-bold px-2 py-0.5"><Star size={11} /> Current</span>
                    )}
                  </div>
                  <div className="mt-1.5 text-xs text-[#64748B] space-y-0.5">
                    <p>Imported {fmtDate(v.imported_at)}{v.document_date ? ` · Letter dated ${v.document_date}` : ""}</p>
                    <p className="flex items-center gap-1.5">
                      <FileText size={12} /> {v.filename || "document"}
                      <a href={`${API}/vault/${v.vault_doc_id}/file`} target="_blank" rel="noreferrer"
                        data-testid={`dlv-file-${v.version_id}`} className="text-brand hover:underline inline-flex items-center gap-0.5">
                        View original <ExternalLink size={11} />
                      </a>
                    </p>
                    <p className="font-mono text-[10px] text-[#94A3B8] break-all" data-testid={`dlv-fp-${v.version_id}`}>fingerprint {v.file_fingerprint?.slice(0, 16)}…</p>
                    {v.correction_count > 0 && <p className="text-[#94A3B8]">Corrected {v.correction_count}× (prior confirmations preserved)</p>}
                  </div>
                </div>
                <div className="flex items-center gap-1.5">
                  {!archived && current !== v.version_id && (
                    <Button size="sm" variant="ghost" className="rounded-lg text-xs h-8" data-testid={`dlv-setcurrent-${v.version_id}`} onClick={() => setCurrentVersion(v.version_id)}>
                      Set current
                    </Button>
                  )}
                  {!archived && (
                    <Button size="sm" variant="outline" className="rounded-lg text-xs h-8" data-testid={`dlv-review-${v.version_id}`} onClick={() => setConfirmFor(v)}>
                      <Pencil size={12} className="mr-1" /> {v.status === "confirmed" ? "Correct" : "Review & confirm"}
                    </Button>
                  )}
                </div>
              </div>
              {(v.status === "confirmed" ? v.confirmed_charges : v.draft_charges).length > 0 && (
                <ul className="mt-3 divide-y divide-[#F1F5F9] rounded-lg border border-[#F1F5F9]">
                  {(v.status === "confirmed" ? v.confirmed_charges : v.draft_charges).map((c) => (
                    <li key={c.id} className="flex items-center justify-between px-3 py-1.5 text-sm">
                      <span className="text-[#334155] truncate">{c.label}</span>
                      <span className="text-navy font-medium tabular-nums">{money(c.amount)}</span>
                    </li>
                  ))}
                </ul>
              )}
            </li>
          ))}
        </ol>
      )}

      {attachOpen && (
        <AttachDialog propertyId={propertyId} open={attachOpen} onOpenChange={setAttachOpen}
          nextSeq={(versions[versions.length - 1]?.seq || 0) + 1} onAttached={() => { setAttachOpen(false); load(); }} />
      )}
      {confirmFor && (
        <ConfirmDialog version={confirmFor} open={!!confirmFor} onOpenChange={(o) => !o && setConfirmFor(null)}
          onConfirmed={() => { setConfirmFor(null); load(); }} />
      )}
      {compareOpen && (
        <CompareDialog propertyId={propertyId} versions={versions.filter((v) => v.status === "confirmed")}
          open={compareOpen} onOpenChange={setCompareOpen} />
      )}
    </div>
  );
}

function AttachDialog({ propertyId, open, onOpenChange, nextSeq, onAttached }) {
  const [docs, setDocs] = useState([]);
  const [picked, setPicked] = useState("");
  const [label, setLabel] = useState(`Version ${nextSeq}`);
  const [docDate, setDocDate] = useState("");
  const [busy, setBusy] = useState(false);
  const [actionId] = useState(() => (window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random()}`));

  useEffect(() => {
    api.get(`/properties/${propertyId}/vault`).then(({ data }) => {
      setDocs(((data && data.documents) || []).filter((d) => d.source === "upload" && d.doc_id));
    }).catch(() => setDocs([]));
  }, [propertyId]);

  const attach = async () => {
    if (!picked) { toast.error("Choose a document from your Vault."); return; }
    setBusy(true);
    try {
      await api.post(`/properties/${propertyId}/deduction-versions`, {
        vault_doc_id: picked, label: label.trim() || `Version ${nextSeq}`,
        document_date: docDate.trim() || null, client_action_id: actionId,
      });
      toast.success("Letter version attached — review the extracted charges to confirm.");
      onAttached();
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't attach that document."); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="dlv-attach-dialog">
        <DialogHeader>
          <DialogTitle>Attach a received letter</DialogTitle>
          <DialogDescription>Choose a document already in your Document Vault. Its original file and fingerprint are preserved.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Vault document</Label>
            <div className="mt-1 max-h-44 overflow-y-auto rounded-xl border border-[#E2E8F0] divide-y" data-testid="dlv-doc-list">
              {docs.length === 0 && <p className="p-3 text-sm text-[#94A3B8]">No Vault documents. Upload the letter to your Document Vault first.</p>}
              {docs.map((d) => (
                <button key={d.doc_id} type="button" data-testid={`dlv-doc-${d.doc_id}`} onClick={() => setPicked(d.doc_id)}
                  className={`w-full flex items-center gap-2 px-3 py-2 text-left text-sm ${picked === d.doc_id ? "bg-[#EFF6FF]" : "hover:bg-[#F8FAFC]"}`}>
                  <FileText size={15} className="text-brand shrink-0" />
                  <span className="min-w-0 flex-1 truncate">{d.filename || "document"}</span>
                  {picked === d.doc_id && <Check size={15} className="text-brand" />}
                </button>
              ))}
            </div>
          </div>
          <div>
            <Label className="text-xs" htmlFor="dlv-label">Version label</Label>
            <Input id="dlv-label" data-testid="dlv-label-input" value={label} onChange={(e) => setLabel(e.target.value)} className="mt-1 h-10 rounded-xl" />
          </div>
          <div>
            <Label className="text-xs" htmlFor="dlv-date">Date on the letter (optional)</Label>
            <Input id="dlv-date" data-testid="dlv-date-input" value={docDate} onChange={(e) => setDocDate(e.target.value)} placeholder="e.g. 2026-06-01" className="mt-1 h-10 rounded-xl" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" className="rounded-xl" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button className="rounded-xl" disabled={busy || !picked} data-testid="dlv-attach-save" onClick={attach}>
            {busy ? <Loader2 size={15} className="mr-1.5 animate-spin" /> : <Plus size={15} className="mr-1.5" />} Attach version
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ConfirmDialog({ version, open, onOpenChange, onConfirmed }) {
  const initial = (version.status === "confirmed" ? version.confirmed_charges : version.draft_charges);
  const [rows, setRows] = useState(
    (initial.length ? initial : [{ label: "", amount: "", explanation: "" }]).map((c) => ({ ...c })));
  const [busy, setBusy] = useState(false);

  const setRow = (i, patch) => setRows((r) => r.map((row, idx) => (idx === i ? { ...row, ...patch } : row)));
  const addRow = () => setRows((r) => [...r, { label: "", amount: "", explanation: "" }]);
  const removeRow = (i) => setRows((r) => r.filter((_, idx) => idx !== i));

  const save = async () => {
    const charges = rows
      .filter((r) => (r.label || "").trim())
      .map((r) => ({ label: r.label.trim(), amount: r.amount === "" || r.amount === null ? null : Number(r.amount),
                     description: r.description || "", explanation: r.explanation || "" }));
    if (!charges.length) { toast.error("Add at least one charge with a label."); return; }
    setBusy(true);
    try {
      await api.post(`/deduction-versions/${version.version_id}/confirm`, { charges });
      toast.success(version.status === "confirmed" ? "Correction saved — prior confirmation preserved." : "Charges confirmed for this version.");
      onConfirmed();
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't confirm the charges."); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="dlv-confirm-dialog">
        <DialogHeader>
          <DialogTitle>Review charges — {version.label}</DialogTitle>
          <DialogDescription>
            These were read from your document. Nothing counts until you confirm it here. Correct anything before confirming.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2 max-h-[50vh] overflow-y-auto">
          {rows.map((r, i) => (
            <div key={i} className="grid grid-cols-[1fr_88px_auto] gap-2 items-start" data-testid={`dlv-row-${i}`}>
              <Input placeholder="Charge label" value={r.label || ""} data-testid={`dlv-row-label-${i}`}
                onChange={(e) => setRow(i, { label: e.target.value })} className="h-9 rounded-lg text-sm" />
              <Input placeholder="Amount" inputMode="decimal" value={r.amount ?? ""} data-testid={`dlv-row-amount-${i}`}
                onChange={(e) => setRow(i, { amount: e.target.value })} className="h-9 rounded-lg text-sm" />
              <button onClick={() => removeRow(i)} className="h-9 w-9 grid place-items-center text-[#94A3B8] hover:text-red-500" aria-label="Remove"><Trash2 size={15} /></button>
              <Input placeholder="Supporting explanation (optional)" value={r.explanation || ""} data-testid={`dlv-row-expl-${i}`}
                onChange={(e) => setRow(i, { explanation: e.target.value })} className="h-9 rounded-lg text-sm col-span-3" />
            </div>
          ))}
          <Button variant="outline" size="sm" className="rounded-lg" data-testid="dlv-add-row" onClick={addRow}><Plus size={13} className="mr-1" /> Add charge</Button>
        </div>
        <DialogFooter>
          <Button variant="outline" className="rounded-xl" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button className="rounded-xl" disabled={busy} data-testid="dlv-confirm-save" onClick={save}>
            {busy ? <Loader2 size={15} className="mr-1.5 animate-spin" /> : <Check size={15} className="mr-1.5" />} Confirm charges
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CompareDialog({ propertyId, versions, open, onOpenChange }) {
  const [aId, setAId] = useState(versions[0]?.version_id || "");
  const [bId, setBId] = useState(versions[versions.length - 1]?.version_id || "");
  const [diff, setDiff] = useState(null);
  const [loading, setLoading] = useState(false);

  const runDiff = useCallback(async () => {
    if (!aId || !bId || aId === bId) { setDiff(null); return; }
    setLoading(true);
    try {
      const { data } = await api.get(`/properties/${propertyId}/deduction-versions/diff`, { params: { a: aId, b: bId } });
      setDiff(data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't compare those versions."); }
    finally { setLoading(false); }
  }, [propertyId, aId, bId]);

  useEffect(() => { runDiff(); }, [runDiff]);

  const decide = async (m, relation) => {
    try {
      await api.post(`/properties/${propertyId}/deduction-versions/match`, {
        version_a_id: m.version_a_id, charge_a_id: m.a_charge.id,
        version_b_id: m.version_b_id, charge_b_id: m.b_charge.id, relation,
      });
      runDiff();
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't save that decision."); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="dlv-compare-dialog">
        <DialogHeader>
          <DialogTitle>Compare letter versions</DialogTitle>
          <DialogDescription>A factual, side-by-side look at what changed between two confirmed versions.</DialogDescription>
        </DialogHeader>
        <div className="flex items-center gap-2 text-sm">
          <select data-testid="dlv-compare-a" value={aId} onChange={(e) => setAId(e.target.value)} className="flex-1 h-9 rounded-lg border border-[#E2E8F0] px-2 bg-white text-navy text-sm">
            {versions.map((v) => <option key={v.version_id} value={v.version_id}>{v.seq}. {v.label}</option>)}
          </select>
          <ArrowRight size={16} className="text-[#94A3B8] shrink-0" />
          <select data-testid="dlv-compare-b" value={bId} onChange={(e) => setBId(e.target.value)} className="flex-1 h-9 rounded-lg border border-[#E2E8F0] px-2 bg-white text-navy text-sm">
            {versions.map((v) => <option key={v.version_id} value={v.version_id}>{v.seq}. {v.label}</option>)}
          </select>
        </div>

        {loading ? (
          <div className="mt-3 flex items-center gap-2 text-sm text-[#64748B]"><Loader2 className="animate-spin" size={16} /> Comparing…</div>
        ) : !diff ? (
          <p className="mt-3 text-sm text-[#94A3B8]">Choose two different versions.</p>
        ) : !diff.comparable ? (
          <p className="mt-3 text-sm text-[#92400E] bg-[#FEF3C7] rounded-lg px-3 py-2" data-testid="dlv-not-comparable">{diff.message}</p>
        ) : (
          <div className="mt-2 space-y-3 max-h-[52vh] overflow-y-auto text-sm" data-testid="dlv-diff-result">
            <DiffTotal total={diff.total_changed} />
            <DiffGroup testid="dlv-added" title="Charge added" color="text-[#15803D]"
              items={diff.added.map((c) => `${c.label} — ${money(c.amount)}`)} />
            <DiffGroup testid="dlv-removed" title="Charge removed" color="text-[#B91C1C]"
              items={diff.removed.map((c) => `${c.label} — ${money(c.amount)}`)} />
            <DiffGroup testid="dlv-amount-changed" title="Amount changed" color="text-navy"
              items={diff.amount_changed.map((c) => `${c.label}: ${money(c.from)} → ${money(c.to)}`)} />
            <DiffGroup testid="dlv-desc-changed" title="Description changed" color="text-navy"
              items={diff.description_changed.map((c) => `${c.label}: “${c.from || "—"}” → “${c.to || "—"}”`)} />
            <DiffGroup testid="dlv-expl-changed" title="Supporting explanation changed" color="text-navy"
              items={diff.explanation_changed.map((c) => `${c.label}: “${c.from || "—"}” → “${c.to || "—"}”`)} />

            {diff.possible_matches.length > 0 && (
              <div className="rounded-xl border border-[#FDE68A] bg-[#FFFBEB] p-3" data-testid="dlv-possible">
                <p className="flex items-center gap-1.5 font-semibold text-[#92400E]"><CircleHelp size={15} /> Possible match — confirmation required</p>
                <p className="mt-1 text-xs text-[#92400E]">These may or may not be the same item. Nothing is classified as added, removed, or changed until you decide.</p>
                <div className="mt-2 space-y-2">
                  {diff.possible_matches.map((m, i) => (
                    <div key={i} data-testid={`dlv-possible-${i}`} className="rounded-lg bg-white border border-[#FDE68A] p-2">
                      <p className="text-[#334155]">{m.a_charge.label} ({money(m.a_charge.amount)}) &harr; {m.b_charge.label} ({money(m.b_charge.amount)})</p>
                      <p className="text-[10px] text-[#92400E]">{m.reason}</p>
                      <div className="mt-1.5 flex gap-2">
                        <Button size="sm" className="h-7 rounded-lg text-xs" data-testid={`dlv-same-${i}`} onClick={() => decide(m, "same")}>Same item</Button>
                        <Button size="sm" variant="outline" className="h-7 rounded-lg text-xs" data-testid={`dlv-diff-${i}`} onClick={() => decide(m, "different")}>Different items</Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <p className="text-xs text-[#94A3B8]">{diff.unchanged_count} charge{diff.unchanged_count === 1 ? "" : "s"} unchanged.</p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function DiffTotal({ total }) {
  return (
    <div className="rounded-xl border border-[#E2E8F0] bg-white p-3 flex items-center justify-between" data-testid="dlv-total">
      <span className="text-[#64748B]">Total of listed charges</span>
      <span className="font-semibold text-navy tabular-nums">
        {money(total.from)} → {money(total.to)}
        {total.changed && <span className="ml-1.5 text-xs text-[#64748B]">({total.delta >= 0 ? "+" : ""}{money(total.delta).replace("$-", "$")})</span>}
      </span>
    </div>
  );
}

function DiffGroup({ title, items, color, testid }) {
  if (!items.length) return null;
  return (
    <div data-testid={testid}>
      <p className={`font-semibold ${color}`}>{title}</p>
      <ul className="mt-1 space-y-1">
        {items.map((t, i) => <li key={i} className="text-[#334155] pl-3 border-l-2 border-[#E2E8F0]">{t}</li>)}
      </ul>
    </div>
  );
}
